import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import {
	Alert,
	Button,
	CircularProgress,
	DialogProps,
	Grid2,
	Snackbar,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { capitalize } from "lodash-es";
import { useShelvesContext } from "../context/ShelvesContext";
import { Shelf, SHELF_INITIAL_VALUES } from "../models/Shelf";
import { deleteShelf, upsertShelf } from "../repositories/ShelvesRepository";

type Props = {
	modal: "new" | "edit" | "delete" | "view" | "";
	onHide: (shouldUpdate: boolean) => void;
};

export const UpsertDeleteViewShelf = ({ modal, onHide }: Props) => {
	const maxWidth: DialogProps["maxWidth"] = "sm";
	const [error, setError] = useState<string>("");
	const [title, setTitle] = useState<string>("CREATE");
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const successfullyMessage = `The shelf was ${modal}d successfully`;

	const {
		state: { shelfSelectedInTree, shelves },
	} = useShelvesContext();

	const { control, reset, setValue, getValues, handleSubmit } = useForm<Shelf>({
		defaultValues: shelfSelectedInTree
			? shelfSelectedInTree
			: SHELF_INITIAL_VALUES,
	});

	const handleCancel = () => {
		onHide(false);
	};

	const onSubmit: SubmitHandler<Shelf> = async (data: Shelf) => {
		setIsSubmitLoading(true);
		if (modal === "delete") {
			const shelfResp = await deleteShelf(shelfSelectedInTree.TreeId as number);
			if (shelfResp.ok) {
				setNotificationMessage(successfullyMessage);
				onHide(true);
			} else {
				setError(shelfResp.message);
			}
		} else {
			const shelfResp = await upsertShelf(data);
			if (shelfResp.ok) {
				setNotificationMessage(successfullyMessage);
				onHide(true);
			} else {
				setError(shelfResp.message);
			}
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (modal !== "") {
			reset();
			setTitle(
				modal === "new"
					? "CREATE"
					: modal === "edit"
						? "EDIT"
						: modal === "delete"
							? "DELETE"
							: "VIEW",
			);
			if (modal !== "new") {
				setValue("TreeId", shelfSelectedInTree.TreeId);
				setValue("ParentTreeId", shelfSelectedInTree.ParentTreeId);
				setValue("Abbrev", shelfSelectedInTree.Abbrev);
				setValue("Title", shelfSelectedInTree.Title);
				setValue("UniqueKey", shelfSelectedInTree.UniqueKey);
				setValue("isLeaf", shelfSelectedInTree.isLeaf);
			} else {
				// If there is a node selected, we take it as parent
				if (shelfSelectedInTree?.TreeId) {
					setValue("ParentTreeId", shelfSelectedInTree.TreeId);
				} else {
					const parentRoot = shelves.find(
						(node: Shelf) => node.ParentTreeId === null,
					);
					if (parentRoot) {
						setValue("ParentTreeId", parentRoot.TreeId);
					}
				}

				const parentKey = shelfSelectedInTree?.UniqueKey || "";
				let nextUniqueKey = "";

				if (parentKey) {
					const siblings = shelves.filter(
						(node: Shelf) => node.ParentTreeId === shelfSelectedInTree?.TreeId,
					);

					const regex = new RegExp(`^${parentKey}(\\d+)$`);
					let maxNumber = 0;

					siblings.forEach((sibling: Shelf) => {
						const match = sibling.UniqueKey?.match(regex);
						if (match) {
							const num = parseInt(match[1], 10);
							if (num > maxNumber) {
								maxNumber = num;
							}
						}
					});

					// A shelf moved out keeps its key, so the next number can be taken elsewhere in the tree
					let nextNumber = maxNumber > 0 ? maxNumber + 1 : 1;
					const isTaken = (key: string) =>
						shelves.some(
							(node: Shelf) => (node.UniqueKey ?? "").trim() === key,
						);
					while (
						nextNumber < 99 &&
						isTaken(`${parentKey}${String(nextNumber).padStart(2, "0")}`)
					) {
						nextNumber++;
					}
					nextUniqueKey = `${parentKey}${String(nextNumber).padStart(2, "0")}`;
				}

				const abbrev = nextUniqueKey.startsWith(parentKey)
					? nextUniqueKey.substring(parentKey.length)
					: nextUniqueKey;

				setValue("Abbrev", abbrev);
				setValue("UniqueKey", nextUniqueKey);
				setValue("Title", `${nextUniqueKey} - `);
			}
		}
	}, [modal]);

	return (
		<>
			{modal !== "" && (
				<HelmetDexteel
					title={`${capitalize(modal.toLowerCase())} Shelf - Shelves`}
				/>
			)}
			<MesfModal
				title={`${title} SHELF`}
				open={modal !== ""}
				maxWidth={maxWidth}
				handleClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<Controller
									name="Abbrev"
									control={control}
									rules={{ required: "Abbreviation is required" }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="Abbreviation"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
											onChange={onChange}
											value={value}
											disabled={modal === "delete" || modal === "view"}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 8 }}>
								<Controller
									name="Title"
									control={control}
									rules={{ required: "Title is required" }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="Title"
											variant="outlined"
											fullWidth
											multiline
											maxRows={1}
											onKeyDown={(e) => {
												if (e.key === "Enter") e.preventDefault();
											}}
											margin="dense"
											autoComplete="off"
											onChange={onChange}
											value={value}
											disabled={modal === "delete" || modal === "view"}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="UniqueKey"
									control={control}
									rules={{ required: "Unique Key is required" }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="Unique Key"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
											onChange={(e) => {
												onChange(e);
												if (modal === "new" || modal === "edit") {
													const currentTitle = getValues("Title") || "";
													const newKey = e.target.value;
													const separatorIndex = currentTitle.indexOf(" - ");
													if (separatorIndex !== -1) {
														// Replace only the part before the first " - "
														const suffix =
															currentTitle.substring(separatorIndex);
														setValue("Title", newKey + suffix);
													} else {
														setValue("Title", newKey);
													}

													// Update Abbrev: difference between newKey and parent's UniqueKey
													let parentKey = "";
													if (modal === "new") {
														parentKey = shelfSelectedInTree?.UniqueKey || "";
													} else {
														const parentShelf = shelves.find(
															(node: Shelf) =>
																node.TreeId ===
																shelfSelectedInTree?.ParentTreeId,
														);
														parentKey = parentShelf?.UniqueKey || "";
													}
													const abbrev = newKey.startsWith(parentKey)
														? newKey.substring(parentKey.length)
														: newKey;
													setValue("Abbrev", abbrev);
												}
											}}
											value={value}
											disabled={modal === "delete" || modal === "view"}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 justifyContent="flex-end" size={12} width={"100%"}>
							<Grid2 container justifyContent="flex-end" spacing={1}>
								<Grid2 size={{ md: 3, xs: 5 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										Cancel
									</Button>
								</Grid2>
								{modal !== "view" ? (
									<Grid2 size={{ md: 3, xs: 5 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color={modal === "delete" ? "error" : "primary"}
											type="submit"
										>
											{modal == "delete" ? "Delete" : "Save"}
										</Button>
									</Grid2>
								) : null}
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={!!notificationMessage}
				autoHideDuration={6000}
				onClose={() => setNotificationMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setNotificationMessage("")} severity="success">
					{notificationMessage}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
